/**
 * Zod Schemas for ETL Integration Response Validation
 *
 * These schemas are used to validate responses from the etl_integrations service
 * at runtime, and to derive TypeScript types via z.infer.
 */
import { z } from 'zod';

const EtlStatusDetailsSchema = z.object({
  // Known status types from AirAmbulanceForm constants (STATUS_TYPES), with z.string() fallback for unknown values
  type: z
    .union([
      z.literal('accepted'),
      z.literal('dispatchReceived'),
      z.literal('dispatchReceivedStandby'),
      z.literal('dispatchReviewing'),
      z.literal('checkingWeather'),
      z.literal('preparingToLaunch'),
      z.literal('enRouteToScene'),
      z.literal('arrivedToScene'),
      z.literal('transporting'),
      z.literal('arrivedToDropoff'),
      z.literal('complete'),
      z.literal('pendingCancellation'),
      z.literal('cancelled'),
      z.literal('dispatchDelayed'),
      z.literal('error'),
      z.literal('cancelPending'),
      z.literal('cancelDisregarded'),
      z.literal('cancelCompleted'),
      z.literal('editLocationPending'),
      z.literal('editLocationDisregarded'),
      z.literal('editLocationCompleted'),
      z.literal('activationRequested'),
      z.literal('checkingWeatherStandby'),
      z.literal('dispatchReviewingStandby'),
      z.literal('waitingForActivation'),
      z.literal('activationPending'),
      z.literal('activationCompleted'),
      z.string(),
    ])
    .nullable()
    .optional(),
  display: z.string().nullable().optional(),
  statusTimeUTC: z.string().nullable().optional(),
});

const EtlVehicleDetailsSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});
export type EtlVehicleDetails = z.infer<typeof EtlVehicleDetailsSchema>;

const EtlProviderDetailsSchema = z.object({
  name: z.string().nullable().optional(),
});
export type EtlProviderDetails = z.infer<typeof EtlProviderDetailsSchema>;

const EtlUrlSchema = z.object({
  transportTracking: z.string().nullable().optional(),
});

const EtlEtaDetailsSchema = z.object({
  display: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  utcTime: z.string().nullable().optional(),
  timeOffset: z.string().nullable().optional(),
});
export type EtlEtaDetails = z.infer<typeof EtlEtaDetailsSchema>;

const EtlDispatchCenterDetailsSchema = z.object({
  name: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
});
export type EtlDispatchCenterDetails = z.infer<
  typeof EtlDispatchCenterDetailsSchema
>;

const EtlUpdateDetailsSchema = z.object({
  status: EtlStatusDetailsSchema.nullable().optional(),
  eta: z.array(EtlEtaDetailsSchema).nullable().optional(),
  vehicle: EtlVehicleDetailsSchema.nullable().optional(),
  provider: EtlProviderDetailsSchema.nullable().optional(),
  dispatchCenter: EtlDispatchCenterDetailsSchema.nullable().optional(),
});

const EtlStatusVerbatimMessageSchema = z.object({
  update: EtlUpdateDetailsSchema.nullable().optional(),
  url: EtlUrlSchema.nullable().optional(),
});
export type EtlStatusVerbatimMessage = z.infer<
  typeof EtlStatusVerbatimMessageSchema
>;

const EtlStatusResponseSchema = z.object({
  id: z.number(),
  gmr_trip_id: z.string(),
  created_at: z.string(),
  ingestion_source: z.string().nullish(),
  verbatim_message: EtlStatusVerbatimMessageSchema.nullable().optional(),
});
export type EtlStatusResponse = z.infer<typeof EtlStatusResponseSchema>;

/**
 * Shared structured address fields carried by every GMR trip-leg location
 * (pickup and dropoff) and by the backend-resolved locations. Both verbatim
 * locations and the resolved locations build on these same fields.
 */
const TripLocationSchema = z.object({
  locationName: z.string().nullable().optional(),
  address1: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
});
export type TripLocation = z.infer<typeof TripLocationSchema>;

// Pickup/dropoff location the backend composites from the order and every
// status update (newest non-empty value per field), so Unite reads one
// resolved value. `display` is backend-composited from GMR webhooks, not a
// raw trip-leg field on the frontend schema.
const ResolvedLocationSchema = TripLocationSchema.extend({
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  display: z.string().nullable().optional(),
});
export type ResolvedLocation = z.infer<typeof ResolvedLocationSchema>;

const ComputedStatusItemSchema = z.object({
  status: z.string(),
  type: z.string(),
  timestamp: z.string(),
});

const ComputedValuesSchema = z.object({
  source: z.string(),
  editEnabled: z.boolean(),
  activateEnabled: z.boolean(),
  cancelEnabled: z.boolean(),
  status: z.string().nullable(),
  eta: z.string().nullable(),
  aircraft: z.string().nullable(),
  trackingUrl: z.string().nullable(),
  pickup: z.string().nullable(),
  dropoff: z.string().nullable(),
  dispatchCenterName: z.string().nullable(),
  dispatchCenterPhone: z.string().nullable(),
  arrivedOnSceneAt: z.string().nullable(),
  statuses: z.array(ComputedStatusItemSchema),
});

const EtlTransportResponseSchema = z.object({
  id: z.number(),
  gmr_transport_id: z.string(),
  account_id: z.string(),
  created_at: z.string(),
  is_test: z.boolean(),
  ingestion_source: z.string().nullish(),
  verbatim_message: z.record(z.unknown()).nullable().optional(),
  resolved_pickup_location: ResolvedLocationSchema.nullable().optional(),
  resolved_dropoff_location: ResolvedLocationSchema.nullable().optional(),
  statuses: z.array(EtlStatusResponseSchema),
  caller_id: z.string().optional().nullable(),
  queue_category_override: z.enum(['active', 'closed']).nullable().optional(),
  queue_category_override_at: z.string().nullable().optional(),
  computed_fields: ComputedValuesSchema,
});

export type ComputedStatusItem = z.infer<typeof ComputedStatusItemSchema>;
export type ComputedValues = z.infer<typeof ComputedValuesSchema>;
export type EtlTransportResponse = z.infer<typeof EtlTransportResponseSchema>;

const EtlTransportTentativeEtaSchema = z.object({
  eta: z.string(),
  providers: z.string(),
});

const EtlErrorEnvelopeSchema = z.object({
  status: z.literal('failure'),
  detail: z.string(),
});

const EtlSuccessEnvelopeSchema = z.object({
  status: z.literal('success'),
});

/**
 * Create an envelope schema that discriminates on `status`.
 * Success: { status: "success", result: <resultSchema> }
 * Failure: { status: "failure", detail: string }
 */
function etlEnvelope<T extends z.ZodTypeAny>(resultSchema: T) {
  return z.discriminatedUnion('status', [
    z.object({ ...EtlSuccessEnvelopeSchema.shape, result: resultSchema }),
    EtlErrorEnvelopeSchema,
  ]);
}

// POST /api/gmr/transports response
export const EtlCreateTransportResponseSchema = etlEnvelope(
  EtlTransportResponseSchema,
);
export type EtlCreateTransportResponse = z.infer<
  typeof EtlCreateTransportResponseSchema
>;
/** This is @see EtlCreateTransportResponse.result when @see EtlCreateTransportResponse.status == "success" */
export type EtlCreateTransportResult = Extract<
  EtlCreateTransportResponse,
  { status: 'success' }
>['result'];

// GET /api/gmr/transports response
export const EtlListTransportsResponseSchema = etlEnvelope(
  z.object({ transports: z.array(EtlTransportResponseSchema) }),
);
export type EtlListTransportsResponse = z.infer<
  typeof EtlListTransportsResponseSchema
>;
export type EtlListTransportsResult = Extract<
  EtlListTransportsResponse,
  { status: 'success' }
>['result'];

// GET /api/gmr/transports/eta response
export const EtlGetTransportTentativeEtaResponseSchema = etlEnvelope(
  EtlTransportTentativeEtaSchema,
);
export type EtlGetTransportTentativeEtaResponse = z.infer<
  typeof EtlGetTransportTentativeEtaResponseSchema
>;
export type EtlGetTransportTentativeEtaResult = Extract<
  EtlGetTransportTentativeEtaResponse,
  { status: 'success' }
>['result'];

// Transport Update Request Schema (PATCH /api/gmr/transports/:id)
export const EtlApiTransportUpdateRequestSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  landingZoneId: z.string().nullable().optional(),
  tripReason: z.string().optional(),
  comments: z.string().optional(),
  pickupLocationComments: z.string().optional(),
  multipleUnites: z.boolean().optional(),
  otherResponders: z.boolean().optional(),
  otherRespondersDeclinedWeather: z.boolean().optional(),
  groundContact: z.string().optional(),
  callerAddress: z.string().optional(),
});
export type EtlApiTransportUpdateRequest = z.infer<
  typeof EtlApiTransportUpdateRequestSchema
>;

// Transport Cancel Request Schema (DELETE /api/gmr/transports/:id)
export const EtlApiTransportCancelRequestSchema = z.object({
  reason: z.string(),
});
export type EtlApiTransportCancelRequest = z.infer<
  typeof EtlApiTransportCancelRequestSchema
>;

// Transport Request Schema (POST /api/gmr/transports)
export const EtlApiTransportRequestSchema = z.object({
  isTest: z.boolean(),
  isStandby: z.boolean(),
  tripReason: z.string().optional(),
  comments: z.string().optional(),
  callerId: z.string().optional(),
  multipleUnites: z.boolean().optional(),
  latitude: z.number(),
  longitude: z.number(),
  landingZoneId: z.string().optional(),
  pickupLocationComments: z.string().optional(),
  otherResponders: z.boolean(),
  otherRespondersDeclinedWeather: z.boolean(),
  groundContact: z.string().optional(),
  callerAddress: z.string().optional(),
});
export type EtlApiTransportRequest = z.infer<
  typeof EtlApiTransportRequestSchema
>;

const TransportVerbatimMessageSchema = z.object({
  tripReason: z.string().nullable().optional(),
  comments: z.string().nullable().optional(),
  requestMethod: z.string().nullable().optional(),
  caller: z
    .object({
      address: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  tripLeg: z
    .array(
      z.object({
        multipleUnits: z.boolean().nullable().optional(),
        pickupLocation: TripLocationSchema.extend({
          latitude: z.number().nullable().optional(),
          longitude: z.number().nullable().optional(),
          comment: z.string().nullable().optional(),
        })
          .nullable()
          .optional(),
        dropoffLocation: TripLocationSchema.nullable().optional(),
        airDetails: z
          .object({
            idenStandBy: z.boolean().nullable().optional(),
            svcWeatherDecline: z.boolean().nullable().optional(),
            svcOtherRespond: z.boolean().nullable().optional(),
            groundContact: z.string().nullable().optional(),
          })
          .nullable()
          .optional(),
      }),
    )
    .nullable()
    .optional(),
});
export type TransportVerbatimMessage = z.infer<
  typeof TransportVerbatimMessageSchema
>;

// Query string parameters for fetching transport tentative eta
export interface EtlTransportTentativeEtaParams {
  latitude: number;
  longitude: number;
}

/**
 * Parse a transport's untyped verbatim_message into a typed object.
 * Returns undefined if the input is missing or fails validation.
 */
function parseTransportVerbatimMessage(
  verbatimMessage: Record<string, unknown> | null | undefined,
): TransportVerbatimMessage | undefined {
  if (!verbatimMessage) return undefined;
  const parsed = TransportVerbatimMessageSchema.safeParse(verbatimMessage);
  if (!parsed.success) {
    // eslint-disable-next-line no-console -- unexpected: verbatim_message failed schema validation. .error logs to sentry
    console.error(
      'parseTransportVerbatimMessage: failed to parse verbatim_message',
      parsed.error,
    );
    return undefined;
  }
  return parsed.data;
}

// Treat empty or whitespace-only strings as absent so GMR-blank fields don't render as " ,   ,".
const isNonBlank = (val: string | null | undefined): val is string =>
  typeof val === 'string' && val.trim().length > 0;

function formatResolvedLocationDisplay(
  location: ResolvedLocation | null | undefined,
): string | null {
  const display = location?.display;
  return isNonBlank(display) ? display.trim() : null;
}

/**
 * Resolve the pickup address shown in Unite for a transport.
 * Uses only the backend composited `resolved_pickup_location.display` so the
 * address stays in sync with GMR. Returns null when display is absent or blank.
 */
function formatTransportPickupAddress(
  transport: Pick<EtlTransportResponse, 'resolved_pickup_location'>,
): string | null {
  return formatResolvedLocationDisplay(transport.resolved_pickup_location);
}

/**
 * Resolve the destination (dropoff) address shown in Unite for a transport.
 * Uses only the backend composited `resolved_dropoff_location.display`.
 * Returns null when display is absent or blank.
 */
function formatTransportDropoffAddress(
  transport: Pick<EtlTransportResponse, 'resolved_dropoff_location'>,
): string | null {
  return formatResolvedLocationDisplay(transport.resolved_dropoff_location);
}

/** Display labels for GMR transport `requestMethod` values from verbatim messages. */
const REQUEST_METHOD_LABELS = {
  weborder: 'transport.net',
  phoneorder: 'PHONE',
} as const satisfies Record<string, string>;
type RequestMethod = keyof typeof REQUEST_METHOD_LABELS;

function hasRequestMethodLabel(source: string): source is RequestMethod {
  return Object.prototype.hasOwnProperty.call(REQUEST_METHOD_LABELS, source);
}

/**
 * Returns true when ingestion_source is unite, case-insensitive.
 */
function ingestionSourceIsUnite(source: string | null | undefined): boolean {
  return typeof source === 'string' && source.trim().toLowerCase() === 'unite';
}

/**
 * Resolve a transport origin label from ingestion source and verbatim request method.
 * - returns "Unite" when `ingestion_source` is `unite`
 * - falls back to `requestMethod` labels when available
 * - returns "Unknown" when neither source is recognized
 */
function formatIngestionSource(
  source: string | null | undefined,
  requestMethod: string | null | undefined = null,
): string {
  if (ingestionSourceIsUnite(source)) {
    return 'Unite';
  }

  const requestMethodKey =
    typeof requestMethod === 'string' ? requestMethod.trim().toLowerCase() : '';
  if (requestMethodKey && hasRequestMethodLabel(requestMethodKey)) {
    return REQUEST_METHOD_LABELS[requestMethodKey];
  }

  return 'Unknown';
}

export {
  parseTransportVerbatimMessage,
  formatTransportPickupAddress,
  formatTransportDropoffAddress,
  formatIngestionSource,
  ingestionSourceIsUnite,
};

/**
 * Status display item derived from ETL response for UI rendering
 */
export interface StatusDisplayItem {
  type?: string;
  display?: string;
  timestamp?: string;
  vehicle?: EtlVehicleDetails;
  provider?: EtlProviderDetails;
  dispatchCenter?: EtlDispatchCenterDetails;
  trackingUrl?: string;
  eta?: EtlEtaDetails[];
}
